// SP-2 WS-7 — "Fix & regenerate" confirm modal for a servicer-returned
// statement. Shows the servicer's return reason and reminds the user to
// correct the underlying data (fees, loan terms) before regenerating.

import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useRegenerateStatement } from "@/hooks/portfolioAdmin/useMonthlyCycle";
import { monthLabel } from "./cycleCopy";

export function RegenerateStatementModal({
  loanId,
  statementId,
  period,
  returnedReason,
  open,
  onClose,
}: {
  loanId: string;
  statementId: string;
  period: string;
  returnedReason: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useRegenerateStatement(loanId);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const month = monthLabel(period);

  const confirm = async () => {
    setError(null);
    try {
      await mutation.mutateAsync(statementId);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "The statement could not be regenerated.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Regenerate the ${month} statement`}
      footer={
        done ? (
          <Button onClick={onClose} data-testid="regenerate-done">
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => void confirm()}
              loading={mutation.isPending}
              data-testid="regenerate-confirm"
            >
              Regenerate statement
            </Button>
          </>
        )
      }
    >
      {done ? (
        <p className="text-body text-onyx m-0" data-testid="regenerate-success">
          Statement regenerated — the new version is waiting for your review.
        </p>
      ) : (
        <div className="flex flex-col gap-space-3" data-testid="regenerate-modal">
          <p className="text-body text-onyx m-0">
            {returnedReason
              ? `The servicer returned this bill: “${returnedReason}”. `
              : "The servicer returned this bill. "}
            Correct the underlying data first (fees, loan terms), then
            regenerate. Regenerating supersedes the returned bill with a fresh
            one built from the loan&apos;s current data.
          </p>
          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
